import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Eye } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fetchAssignedTeams } from '../../api/chat';

interface Team {
  _id: string;
  teamName: string;
  leader: { username: string; name: string; email: string };
  members: any[];
  status?: string;
  hackathonId?: string;
}

export function AssignTeam() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!token) return;
    
    setLoading(true);
    fetchAssignedTeams(token)
      .then((data) => {
        setTeams(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error('Failed to fetch teams:', err);
        setTeams([]);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const filteredTeams = teams.filter((team) =>
    team.teamName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    team.leader?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleViewDetails = (team: Team) => {
    navigate(`/mentor-dashboard/team-details/${team._id}`, { state: { team } });
  };

  return (
    <div className="w-full" style={{ minHeight: 0, minWidth: 0 }}>
      <div className="bg-[#F8FAFC] px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10" style={{ minHeight: 0, minWidth: 0 }}>
        <div className="mx-auto max-w-6xl space-y-6 sm:space-y-8">
        <div className="space-y-2 sm:space-y-3">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Assigned Teams</h1>
          <p className="max-w-2xl text-xs sm:text-sm text-slate-600">Manage and review teams assigned to you</p>
        </div>

        {/* Search Bar */}
        <div className="flex gap-3 sm:gap-4 flex-col sm:flex-row" style={{ minWidth: 0 }}>
          <div className="flex-1 relative" style={{ minWidth: 0 }}>
            <Search className="absolute left-3 sm:left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 flex-shrink-0" />
            <input
              type="search"
              placeholder="Search by team name or leader..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-[12px] border border-slate-200 bg-white px-3 sm:px-4 py-2 sm:py-3 pl-9 sm:pl-11 text-xs sm:text-sm text-slate-700 shadow-sm outline-none transition focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
            />
          </div>
        </div>

        {/* Teams List */}
        <div className="space-y-3 sm:space-y-4" style={{ minWidth: 0 }}>
          {loading ? (
            <div className="rounded-[20px] bg-white p-6 sm:p-8 text-center shadow-sm">
              <p className="text-slate-600 text-sm">Loading teams...</p>
            </div>
          ) : filteredTeams.length === 0 ? (
            <div className="rounded-[20px] bg-white p-6 sm:p-8 text-center shadow-sm">
              <p className="text-slate-600 text-sm">
                {teams.length === 0 ? 'No teams assigned yet' : 'No teams match your search'}
              </p>
            </div>
          ) : (
            filteredTeams.map((team) => (
              <div
                key={team._id}
                className="rounded-[20px] border border-slate-200 bg-white p-4 sm:p-6 shadow-[0_1px_4px_rgba(0,0,0,0.08)] hover:shadow-md transition"
                style={{ minWidth: 0 }}
              >
                <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-start lg:justify-between" style={{ minWidth: 0 }}>
                  <div className="space-y-2 sm:space-y-3 flex-1 min-w-0">
                    <h2 className="text-lg sm:text-xl font-semibold text-slate-900 break-words">{team.teamName}</h2>
                    <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 text-xs sm:text-sm text-slate-500" style={{ minWidth: 0 }}>
                      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 flex-shrink-0">
                        <Users className="h-4 w-4 text-slate-500 flex-shrink-0" />
                        Leader: {team.leader?.name || 'N/A'}
                      </span>
                      <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 flex-shrink-0">
                        <span className="h-4 w-4 rounded-full bg-slate-500 flex-shrink-0" />
                        {team.members?.length || 0} members
                      </span>
                    </div>
                    {team.leader?.email && (
                      <p className="text-xs text-slate-500 break-words">Email: {team.leader.email}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleViewDetails(team)}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 flex-shrink-0 whitespace-nowrap"
                  >
                    <Eye className="h-4 w-4 flex-shrink-0" />
                    View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
